import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search as SearchIcon, ShieldAlert, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/features/admin/api";
import { formatApiError } from "@/features/auth/formatError";
import { Input } from "@/components/ui/input";

export default function AdminCustomers() {
    const qc = useQueryClient();
    const [search, setSearch] = useState("");
    const [blocked, setBlocked] = useState<string>("");
    const [page, setPage] = useState(1);
    const pageSize = 20;

    const { data, isLoading } = useQuery({
        queryKey: ["admin", "customers", search, blocked, page],
        queryFn: () =>
            adminApi.listCustomers({
                search: search || undefined,
                blocked: blocked === "" ? undefined : blocked === "yes",
                page,
                page_size: pageSize,
            }),
        placeholderData: (prev) => prev,
    });

    const blockMut = useMutation({
        mutationFn: ({ id, is_blocked }: { id: string; is_blocked: boolean }) =>
            adminApi.blockCustomer(id, is_blocked),
        onSuccess: (u) => {
            qc.invalidateQueries({ queryKey: ["admin", "customers"] });
            toast.success(u.is_blocked ? "Customer blocked" : "Customer unblocked");
        },
        onError: (err) => toast.error(formatApiError(err, "Couldn't update customer")),
    });

    const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;

    return (
        <div data-testid="admin-customers-page" className="space-y-5">
            <div>
                <h1 className="text-2xl font-extrabold sm:text-3xl">Customers</h1>
                <p className="text-sm text-muted-foreground">
                    {data ? `${data.total} customers` : "Loading…"}
                </p>
            </div>

            <div className="flex flex-wrap gap-3">
                <div className="relative flex-1 min-w-[240px]">
                    <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        className="pl-9"
                        placeholder="Search by name, email or phone…"
                        data-testid="admin-customers-search"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1);
                        }}
                    />
                </div>
                <select
                    value={blocked}
                    onChange={(e) => {
                        setBlocked(e.target.value);
                        setPage(1);
                    }}
                    className="h-10 rounded-full border border-border bg-background px-3 text-sm"
                    data-testid="admin-customers-filter"
                >
                    <option value="">All customers</option>
                    <option value="no">Active</option>
                    <option value="yes">Blocked</option>
                </select>
            </div>

            <div className="overflow-x-auto rounded-2xl border border-border/70 bg-card">
                <table className="min-w-full text-sm">
                    <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <tr>
                            <th className="px-4 py-3">Customer</th>
                            <th className="px-4 py-3">Phone</th>
                            <th className="px-4 py-3">Role</th>
                            <th className="px-4 py-3">Orders</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border/60">
                        {isLoading ? (
                            <tr>
                                <td colSpan={6} className="p-6 text-center text-sm text-muted-foreground">
                                    Loading customers…
                                </td>
                            </tr>
                        ) : data && data.items.length > 0 ? (
                            data.items.map((c) => (
                                <tr key={c.id} data-testid={`admin-customer-row-${c.id}`}>
                                    <td className="px-4 py-3">
                                        <p className="font-semibold">{c.full_name || "—"}</p>
                                        <p className="text-[11px] text-muted-foreground">{c.email}</p>
                                    </td>
                                    <td className="px-4 py-3 text-xs">{c.phone || "—"}</td>
                                    <td className="px-4 py-3 text-xs uppercase text-muted-foreground">
                                        {c.role.replace("_", " ")}
                                    </td>
                                    <td className="px-4 py-3 font-semibold">{c.order_count}</td>
                                    <td className="px-4 py-3">
                                        <span
                                            className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold ${
                                                c.is_blocked
                                                    ? "bg-rose-500/10 text-rose-700"
                                                    : "bg-emerald-500/10 text-emerald-700"
                                            }`}
                                        >
                                            {c.is_blocked ? (
                                                <>
                                                    <ShieldAlert className="h-3 w-3" /> Blocked
                                                </>
                                            ) : (
                                                <>
                                                    <ShieldCheck className="h-3 w-3" /> Active
                                                </>
                                            )}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex justify-end">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    blockMut.mutate({ id: c.id, is_blocked: !c.is_blocked })
                                                }
                                                data-testid={`admin-customer-block-${c.id}`}
                                                disabled={blockMut.isPending}
                                                className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                                                    c.is_blocked
                                                        ? "bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/20"
                                                        : "bg-error/10 text-error hover:bg-error/20"
                                                }`}
                                            >
                                                {c.is_blocked ? "Unblock" : "Block"}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={6} className="p-6 text-center text-sm text-muted-foreground">
                                    No customers match.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Page {page} of {totalPages}</span>
                <div className="flex gap-2">
                    <button
                        disabled={page === 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        className="rounded-full border border-border px-3 py-1 disabled:opacity-40"
                    >
                        Prev
                    </button>
                    <button
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => p + 1)}
                        className="rounded-full border border-border px-3 py-1 disabled:opacity-40"
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
}
